import { useTranslation } from 'react-i18next';
import { Bug, Lightbulb, MessageCircleQuestion, Plus } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import type { ConversationCategory, VendorConversation } from '../../../common/models/support.model';
import { ConversationList } from './ConversationList';

const STARTERS: { category: ConversationCategory; icon: typeof Bug; labelKey: string }[] = [
  { category: 'bug', icon: Bug, labelKey: 'SUPPORT.HOME.STARTER_BUG' },
  { category: 'feature', icon: Lightbulb, labelKey: 'SUPPORT.HOME.STARTER_FEATURE' },
  { category: 'question', icon: MessageCircleQuestion, labelKey: 'SUPPORT.HOME.STARTER_QUESTION' },
];

/** First screen: a way to start, and the conversations so far. */
export function SupportHome({
  firstName,
  conversations,
  loaded,
  onNew,
  onOpen,
  showList = true,
}: {
  showList?: boolean;
  firstName?: string;
  conversations: VendorConversation[];
  loaded: boolean;
  onNew: (category?: ConversationCategory) => void;
  onOpen: (id: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
      <div className="px-5 pb-4 pt-5">
        <p className="text-sm text-muted-foreground">{firstName ? t('SUPPORT.HOME.HELLO_NAME', { name: firstName }) : t('SUPPORT.HOME.HELLO')}</p>
        <h2 className="font-heading text-xl font-medium tracking-tight">{t('SUPPORT.HOME.TITLE')}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{t('SUPPORT.HOME.SUBTITLE')}</p>

        <div className="mt-4 grid grid-cols-3 gap-2">
          {STARTERS.map(({ category, icon: Icon, labelKey }) => (
            <button
              key={category}
              type="button"
              onClick={() => onNew(category)}
              className="flex flex-col items-start gap-2 rounded-xl border bg-card p-3 text-start text-xs transition-colors hover:bg-muted/60"
            >
              <span className="flex size-8 items-center justify-center rounded-lg border bg-muted/50">
                <Icon className="size-4 text-muted-foreground" />
              </span>
              <span className="font-medium leading-tight">{t(labelKey)}</span>
            </button>
          ))}
        </div>
        <Button className="mt-3 w-full" variant="outline" onClick={() => onNew()}>
          <Plus data-icon="inline-start" />
          {t('SUPPORT.HOME.NEW')}
        </Button>
      </div>

      {showList && <div className="border-t px-2 pb-3 pt-3">
        <p className="px-3 pb-1 text-xs font-medium text-muted-foreground">{t('SUPPORT.HOME.YOUR_CONVERSATIONS')}</p>
        {!loaded ? (
          <div className="flex flex-col gap-2 px-3 py-2">
            <Skeleton className="h-10" />
            <Skeleton className="h-10" />
          </div>
        ) : conversations.length ? (
          <ConversationList conversations={conversations} onOpen={onOpen} />
        ) : (
          <p className="px-3 py-4 text-sm text-muted-foreground">{t('SUPPORT.HOME.NONE_YET')}</p>
        )}
      </div>}
    </div>
  );
}
